export interface FieldError {
  field: string;
  message: string;
}

/** Every failed API call is normalised to this. Components branch on `code`, never on `message`. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: FieldError[];
  readonly requestId?: string;
  readonly retryAfter?: number;

  constructor(init: {
    status: number;
    code: string;
    message: string;
    details?: FieldError[];
    requestId?: string;
    retryAfter?: number;
  }) {
    super(init.message);
    this.name = "ApiError";
    this.status = init.status;
    this.code = init.code;
    this.details = init.details;
    this.requestId = init.requestId;
    this.retryAfter = init.retryAfter;
  }
}

interface ErrorBody {
  error?: { code?: string; message?: string; details?: FieldError[] };
}

/** Build an ApiError from the pieces of a failed HTTP exchange (fetch Response or XHR). */
export function apiErrorFromParts(parts: {
  status: number;
  statusText?: string;
  bodyText?: string;
  requestId?: string | null;
  retryAfterHeader?: string | null;
}): ApiError {
  let body: ErrorBody = {};
  try {
    body = JSON.parse(parts.bodyText ?? "") as ErrorBody;
  } catch {
    // Non-JSON error (proxy page, gateway): fall through to defaults.
  }
  const retry = Number(parts.retryAfterHeader);
  return new ApiError({
    status: parts.status,
    code: body.error?.code ?? (parts.status >= 500 ? "INTERNAL_ERROR" : "UNKNOWN_ERROR"),
    message: body.error?.message ?? parts.statusText ?? "",
    details: body.error?.details,
    requestId: parts.requestId ?? undefined,
    retryAfter: Number.isFinite(retry) && retry > 0 ? retry : undefined,
  });
}

export async function toApiError(response: Response): Promise<ApiError> {
  let bodyText = "";
  try {
    bodyText = await response.clone().text();
  } catch {
    // unreadable body
  }
  return apiErrorFromParts({
    status: response.status,
    statusText: response.statusText,
    bodyText,
    requestId: response.headers.get("X-Request-ID"),
    retryAfterHeader: response.headers.get("Retry-After"),
  });
}
